import { poNeedsInvoiceRecord } from "@/lib/manufacturing/purchaseOrderInvoice";
import { poNeedsDeliveryConfirm } from "@/lib/manufacturing/purchaseOrderSpend";
import { isPlantMaterialRequest, plantRequestInboxBlurb } from "@/lib/manufacturing/plantMaterialRequest";
import type { MfgPoAttachmentKind, MfgPurchaseOrder } from "@/lib/manufacturing/purchaseOrderTypes";

export type PurchaseOrderQueueFilter =
  | "open"
  | "requests"
  | "needs_invoice"
  | "needs_delivery"
  | "archived"
  | "all";

const OPEN_STATUSES = new Set(["draft", "issued", "confirmed", "partially_received"]);

export function poHasAttachmentKind(
  order: Pick<MfgPurchaseOrder, "attachment_kinds" | "attachments">,
  kind: MfgPoAttachmentKind
): boolean {
  if (order.attachment_kinds?.includes(kind)) return true;
  return (order.attachments ?? []).some((a) => a.kind === kind);
}

export function filterPurchaseOrderQueue(
  orders: readonly MfgPurchaseOrder[],
  filter: PurchaseOrderQueueFilter
): MfgPurchaseOrder[] {
  if (filter === "all") return [...orders];
  if (filter === "requests") {
    return orders.filter((o) => isPlantMaterialRequest(o) && OPEN_STATUSES.has(o.status));
  }
  if (filter === "open") return orders.filter((o) => OPEN_STATUSES.has(o.status));
  if (filter === "archived") return orders.filter((o) => o.status === "cancelled");
  if (filter === "needs_delivery") {
    return orders.filter((o) => poNeedsDeliveryConfirm(o) && o.status !== "received");
  }
  return orders.filter((o) => poNeedsInvoiceRecord(o));
}

export function purchaseOrderQueueHint(order: MfgPurchaseOrder): string {
  const hint = purchaseOrderQueueHintBody(order);
  if (!order.finalized_at || order.status === "cancelled" || order.status === "received") return hint;
  if (hint === "draft" || hint === "awaiting receive") return "finalized";
  return hint.startsWith("finalized") ? hint : `finalized · ${hint}`;
}

function purchaseOrderQueueHintBody(order: MfgPurchaseOrder): string {
  if (isPlantMaterialRequest(order) && order.status === "draft") {
    if (order.acknowledged_at) return "request · acknowledged";
    const blurb = plantRequestInboxBlurb(order);
    return blurb === "Material request" ? "plant request" : blurb;
  }
  if (order.status === "draft") return "draft";
  if (order.status === "cancelled") {
    const reason = (order.cancel_reason ?? "").trim();
    return reason ? `archived · ${reason}` : "archived";
  }
  if (order.status === "received") return "accepted into inventory";
  if (poNeedsInvoiceRecord(order)) return "needs invoice";
  if (poNeedsDeliveryConfirm(order)) return "waiting delivery";
  const terms = (order.invoice_pay_terms ?? "").trim();
  if (terms) return `invoiced · ${terms}`;
  if (order.status === "issued" || order.status === "confirmed") return "awaiting receive";
  return order.status.replace(/_/g, " ");
}
